import React, { useState, useEffect, useRef } from "react";
import ChatBotAI from "../../assets/images/ChatBotAI.svg";
import aiChatIcon from "../../assets/images/aiChatIcon.svg";
import aiUserIcon from "../../assets/images/aiUserIcon.svg";
import ChatbotGIF from "../../assets/FinalAstraGIf.gif"
import { getChatHistory, sendChatMessage } from "../../shared/_services/common.services";
import { readCurrentUser } from "../../shared/_helpers/currentUser";

// SEC-025 (Backend#70): the server only calls OpenAI when AI_CHATBOT_ENABLED is
// exactly 'true'. Otherwise /chatboat/* answers 503 { code: 'AI_DISABLED' }.
// The first time that is seen the open chatbox says so plainly, and a
// session-scoped flag hides the floating button on the remaining onboarding
// screens, so it stops inviting questions it cannot answer. The flag holds the
// id of the account that saw AI_DISABLED, not a bare "true": sessionStorage
// outlives a logout, and another account signing in on the same tab must get
// its own answer from the server. Any other failure
// is temporary: say so immediately (no fake "thinking" delay) and keep the
// assistant available.
export const AI_CHATBOT_DISABLED_KEY = "astra.aiChatbotDisabled";
export const AI_DISABLED_COPY =
    "The AI assistant is turned off for now. You can keep answering the questions without it.";
export const AI_UNAVAILABLE_COPY = "The assistant isn't available right now. Please try again later.";

export function isAiDisabledError(error) {
    return error?.response?.status === 503 && error?.response?.data?.code === "AI_DISABLED";
}

function signedInUserId() {
    return readCurrentUser()?.user?._id || "";
}

function readDisabledFlag() {
    try {
        const flaggedFor = sessionStorage.getItem(AI_CHATBOT_DISABLED_KEY);
        return flaggedFor !== null && flaggedFor === signedInUserId();
    } catch (_) {
        return false;
    }
}

function writeDisabledFlag() {
    try {
        sessionStorage.setItem(AI_CHATBOT_DISABLED_KEY, signedInUserId());
    } catch (_) {
        // A store we cannot write only means the button reappears next screen.
    }
}

const AIChatBot = ({ questionName, options, firstMsg }) => {
    const greeting = firstMsg || "Hi! I'm here to help you with your financial priorities.";
    const [hiddenAtMount] = useState(readDisabledFlag);
    const [aiDisabled, setAiDisabled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([{ type: "ai", text: greeting }]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const chatRef = useRef(null);
    const messagesEndRef = useRef(null);

    const markDisabled = () => {
        writeDisabledFlag();
        setAiDisabled(true);
    };

    // Close chatbot on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chatRef.current && !chatRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    // Fetch history when chat is opened OR when questionName changes
    useEffect(() => {
        const fetchHistory = async () => {
            if (!questionName) return;

            try {
                const currentUser = JSON.parse(localStorage.getItem("currentUser"));
                const userId = currentUser?.user?._id;

                const history = await getChatHistory(userId, questionName);

                setMessages(history.length > 0 ? history : [{ type: "ai", text: greeting }]);
            } catch (error) {
                if (isAiDisabledError(error)) {
                    markDisabled();
                    return;
                }
                setMessages([{ type: "ai", text: greeting }]);
            }
        };

        if (isOpen && !aiDisabled) fetchHistory();
    }, [questionName, isOpen, firstMsg]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-scroll to bottom
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
        }
    }, [messages]);

    const sendMessage = async () => {
        if (aiDisabled || !input.trim()) return;

        if (/^\d+$/.test(input.trim())) {
            setMessages((prev) => [
                ...prev,
                { type: "ai", text: "For better assistance, please enter a valid financial question or statement." }
            ]);
            setInput("");
            return;
        }

        const userMsg = { type: "user", text: input };
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setLoading(true);

        try {
            const currentUser = JSON.parse(localStorage.getItem("currentUser"));
            const userId = currentUser?.user?._id;

            const aiReply = await sendChatMessage(userId, questionName, options, input);

            setTimeout(() => {
                setMessages((prev) => [...prev, { type: "ai", text: aiReply }]);
                setLoading(false);
            }, 3000);
        } catch (error) {
            setLoading(false);
            if (isAiDisabledError(error)) {
                markDisabled();
                return;
            }
            setMessages((prev) => [...prev, { type: "ai", text: AI_UNAVAILABLE_COPY }]);
        }
    };

    if (hiddenAtMount) return null;

    return (
        <div className="ai-chatbot-container">
            {/* Floating button */}
            <button className="ai-chatbot-button" onClick={() => setIsOpen(!isOpen)}>
                <img src={ChatbotGIF} alt="ChatBot" />
            </button>

            {/* Chatbox */}
            <div ref={chatRef} className={`ai-chatbox ${isOpen ? "open" : "close"}`}>
                <div className="ai-chatbox-header justify-content-between">
                    <div className="d-flex align-items-center gap-10">
                        <img src={ChatBotAI} alt="AI" width="20" />
                        AI Financial Assistant
                    </div>
                    <button style={{ background: 'transparent', border: 'none', cursor: 'pointer' }} onClick={() => setIsOpen(false)}>
                        ❌
                    </button>
                </div>

                <div className="ai-chatbox-body" ref={messagesEndRef}
                >
                    {aiDisabled ? (
                        <div className="ai-rcvd-message" role="status">
                            <img src={aiChatIcon} alt="ai" />
                            <div>{AI_DISABLED_COPY}</div>
                        </div>
                    ) : (
                        messages.map((msg, i) =>
                            msg.type === "ai" ? (
                                <div key={i} className="ai-rcvd-message">
                                    <img src={aiChatIcon} alt="ai" />
                                    <div>{msg.text}</div>
                                </div>
                            ) : (
                                <div key={i} className="user-sent-message">
                                    <div>{msg.text}</div>
                                    <img src={aiUserIcon} alt="user" />
                                </div>
                            )
                        )
                    )}

                    {loading && !aiDisabled && (
                        <div className="ai-rcvd-message">
                            <img src={aiChatIcon} alt="ai" />
                            <div className="ai-loading">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="ai-chatbox-input">
                    <input
                        type="text"
                        placeholder={`Enter your message, I'm here to help you`}
                        value={input}
                        disabled={aiDisabled}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    />
                    <button type="submit" onClick={sendMessage} disabled={aiDisabled}>
                        ➤
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIChatBot;
